import { useMemo, useState } from "react"
import { View, Pressable, ActivityIndicator } from "react-native"
import { useLocalSearchParams } from "expo-router"
import * as Burnt from "burnt"

import { Header, MCIcon, ScrollView, Text } from "@src/components"
import { Alert } from "@src/components/ui/confirm"
import { ar } from "@src/i18n/ar"
import { sharedStyles, trpc } from "@src/utils"
import { SafeAreaView } from "react-native-safe-area-context"

export default function NormalizeStoragePlace() {
  const params = useLocalSearchParams<{ id: string }>()
  const storagePlaceId = Number.parseInt(params.id, 10)
  const utils = trpc.useUtils()

  const denormalized = trpc.storagePlaces.listDenormalizedProducts.useQuery({ storagePlaceId })
  const normalizeProduct = trpc.inventory.normalizeProduct.useMutation()
  const [pendingId, setPendingId] = useState<number | null>(null)

  const entries = useMemo(() => {
    const map = new Map<number, { baseId: number; baseName: string; baseQty: number; derived: { id: number; name: string; quantity: number; value: number }[] }>()
    for (const item of denormalized.data ?? []) {
      const base = item.baseProduct
      if (!base) continue
      let entry = map.get(base.id)
      if (!entry) {
        entry = { baseId: base.id, baseName: base.name, baseQty: base.quantity ?? 0, derived: [] }
        map.set(base.id, entry)
      }
      entry.derived.push({ id: item.id, name: item.name, quantity: item.quantity, value: item.value ?? 1 })
    }
    return [...map.values()]
  }, [denormalized.data])

  function handleNormalize(entry: { baseId: number; baseName: string; resultingQty: number }) {
    Alert.alert(`${ar.normalizeProduct}: ${entry.baseName}`, `${ar.afterNormalization} → ×${entry.resultingQty}`, [
      { text: ar.no, style: "cancel" },
      { text: ar.yes, style: "destructive", onPress: () => confirmNormalize(entry.baseId) },
    ])
  }

  function confirmNormalize(baseId: number) {
    setPendingId(baseId)
    normalizeProduct.mutate(
      { storagePlaceId, baseProductId: baseId },
      {
        onSuccess: () => {
          Burnt.toast({ title: ar.done, preset: "done" })
          utils.storagePlaces.listDenormalizedProducts.invalidate({ storagePlaceId })
          utils.storagePlaces.listProducts.invalidate()
        },
        onError: (error) => Burnt.toast({ title: error.message, preset: "error" }),
        onSettled: () => setPendingId(null),
      },
    )
  }

  if (denormalized.isLoading || denormalized.data?.length === 0) {
    return (
      <>
        <Header title={ar.normalizeStoragePlaces} />
        {denormalized.isLoading ? (
          <ActivityIndicator className="mt-4" />
        ) : (
          <View className="flex-1 items-center justify-center p-8">
            <MCIcon name="check-circle-outline" size={56} color="#10B981" />
            <Text className="text-center font-bold mt-4 text-gray-500">{ar.noProductsNeedNormalization}</Text>
          </View>
        )}
      </>
    )
  }

  return (
    <>
      <Header title={ar.normalizeStoragePlaces} />

      <ScrollView className="flex-1 bg-gray-50" contentContainerStyle={sharedStyles.contentContainer} onRefresh={() => denormalized.refetch()}>
        {entries.map((entry) => {
          const unitsToAdd = entry.derived.reduce((sum, d) => sum + d.quantity * d.value, 0)
          const resultingQty = entry.baseQty + unitsToAdd
          const isPending = pendingId === entry.baseId

          return (
            <View key={entry.baseId} className="bg-white rounded-lg border border-gray-100 shadow-xs p-2 mb-1">
              {/* Base (first row) + derived products */}
              <View className="gap-2">
                <View className="flex-row items-center">
                  <View className="w-1.5 h-1.5 rounded-full bg-emerald-500 me-2" />
                  <Text className="flex-1 font-bold text-gray-900" numberOfLines={1}>
                    {entry.baseName}
                  </Text>
                  <Text className="font-semibold text-gray-900">{entry.baseQty}</Text>
                  <Text className="mx-1 text-orange-500">×</Text>
                  <Text className="text-xs ms-2 text-blue-500">1</Text>
                </View>

                {entry.derived.map((d) => (
                  <View key={d.id} className="flex-row items-center">
                    <View className="w-1.5 h-1.5 rounded-full bg-orange-500 me-2" />
                    <Text className="flex-1 text-gray-900" numberOfLines={1}>
                      {d.name}
                    </Text>
                    <Text className="text-gray-600">{d.quantity}</Text>
                    <Text className="mx-1 text-orange-500">×</Text>
                    <Text className="text-xs ms-2 text-blue-500">{d.value}</Text>
                  </View>
                ))}
              </View>

              {/* Divider + result */}
              <View className="mt-3 pt-3 border-t border-dashed border-gray-200 flex-row items-center justify-between">
                <View className="flex-row items-center gap-1.5">
                  <MCIcon name="arrow-collapse-down" size={16} color="#10B981" />
                  <Text className="text-sm font-semibold text-gray-500">{ar.afterNormalization}</Text>
                </View>
                <Text className="font-bold text-emerald-600">
                  {entry.baseName} → ×{resultingQty}
                </Text>
              </View>

              {/* Normalize */}
              <Pressable
                onPress={() => handleNormalize({ baseId: entry.baseId, baseName: entry.baseName, resultingQty })}
                disabled={normalizeProduct.isPending}
                className="mt-3 flex-row items-center justify-center gap-2 bg-orange-50 border border-orange-200 rounded-lg py-2.5 active:opacity-70"
              >
                {isPending ? <ActivityIndicator size="small" color="#F97316" /> : <MCIcon name="auto-fix" size={20} color="#F97316" />}
                <Text className="font-bold text-orange-600">{ar.normalizeProduct}</Text>
              </Pressable>
            </View>
          )
        })}
        <SafeAreaView />
      </ScrollView>
    </>
  )
}
